import { forwardRef, useState, useEffect } from 'react';
import { SquareCheckBox } from '@atoms/SquareCheckbox/SquareCheckBox';
import { Label } from '@atoms/Label/Label';
import { InlineErrorMessage } from '@atoms/Errors/InlineErrorMessage';
import { ErrorMessages } from '@types';

export type CheckboxOption = {
  id: string;
  name: string;
  label: string | React.ReactNode;
  disabled?: boolean;
  checked: boolean;
};

export type CheckBoxInputProps = {
  options: CheckboxOption[];
  onChange: (selectedOptions: CheckboxOption[]) => void;
  errorPath?: string;
  errorMessages?: ErrorMessages[];
  isLabelTextTitle?: boolean;
  title?: string;
  description?: string;
  maxOptions?: number;
};

export const CheckBoxInput = forwardRef<HTMLDivElement, CheckBoxInputProps>(
  (
    {
      title,
      isLabelTextTitle,
      description,
      options,
      onChange,
      errorPath,
      errorMessages,
      maxOptions = 1,
    },
    ref,
  ) => {
    const [internalOptions, setInternalOptions] =
      useState<CheckboxOption[]>(options);

    useEffect(() => {
      const checkedOptionsCount = options.filter(
        (option) => option.checked,
      ).length;
      const shouldDisableUnselected = checkedOptionsCount >= maxOptions;
      const updatedOptions = options.map((option) => ({
        ...option,
        disabled: shouldDisableUnselected ? !option.checked : option.disabled,
      }));
      setInternalOptions(updatedOptions);
    }, [options, maxOptions]);

    const handleChange = (changedId: string, checked: boolean) => {
      const updatedOptions = internalOptions.map((option) =>
        option.id === changedId ? { ...option, checked } : option,
      );

      const selectedOptionsCount = updatedOptions.filter(
        (option) => option.checked,
      ).length;

      const shouldDisableUnselected = selectedOptionsCount >= maxOptions;

      const optionsWithUpdatedDisabledState = updatedOptions.map((option) => ({
        ...option,
        disabled: shouldDisableUnselected && !option.checked ? true : false,
      }));

      setInternalOptions(optionsWithUpdatedDisabledState);

      onChange(
        optionsWithUpdatedDisabledState.filter((option) => option.checked),
      );
    };

    return (
      <fieldset className="my-4" aria-describedby="checkbox-hint">
        {title && (
          <Label isLegend text={title} isTitle={isLabelTextTitle ?? true} />
        )}
        {description && (
          <div
            id="checkbox-hint"
            data-testid="description"
            className="text-text-body text-bodyLg mb-4 mt-4 pb-2"
          >
            {description}
          </div>
        )}
        {<InlineErrorMessage path={errorPath} messages={errorMessages} />}
        {internalOptions.map((option) => (
          <div key={option.id} className="my-3 flex gap-x-3">
            <SquareCheckBox
              id={option.id}
              name={option.name}
              checked={option.checked}
              onChange={(e) => handleChange(option.id, e.target.checked)}
              disabled={option.disabled}
            />
            <label
              className="text-bodyLg text-text-body mt-1.5 peer-disabled:cursor-not-allowed"
              htmlFor={option.id}
            >
              {option.label}
            </label>
          </div>
        ))}
      </fieldset>
    );
  },
);

CheckBoxInput.displayName = 'CheckBoxInput';
